import { action, computed, observable } from "mobx";
import { DbRecordedEvent, UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { DbEventsTableReader } from "../../State/EvidenceDB/DbEventsTableReader";
import { NetworkEventDebuggerNetworkRequestWillBeSentExtraInfo, NetworkEventOnSendHeaders } from "../../lib/Events";
import Protocol from "devtools-protocol";
import { getRegisteredDomain } from "../../lib/utils/domains";
import { mapGetOrCreate } from "../../lib/utils/mapGetOrCreate";
import { executeEventually } from "../../lib/utils/executeEventually";
import { NewEventAddedToDatabase } from "../../State/StateEvents";

type CookieName = string;
type Cookie = Protocol.Network.Cookie;

export class RecordingCookiesSent {
	highestEventIndexProcessed: number = -1;

	_cookiesBySourceAndDestinationRegisteredDomain = observable.map<string, Map<string, Map<CookieName, {name: string; value: string}>>>();
	@computed get cookiesByRegisteredDomain() { return this._cookiesBySourceAndDestinationRegisteredDomain; }

	processNetworkRequestWillBeSentExtraInfoEvent = action ( (e: DbRecordedEvent<NetworkEventDebuggerNetworkRequestWillBeSentExtraInfo>) => {
		for (const associatedCookie of e.raw.associatedCookies) {
			if (associatedCookie.blockedReasons.length > 0) {
				continue;
			}
			const sourceRegisteredDomain = getRegisteredDomain(e.tab.url);
			const destinationRegisteredDomain = getRegisteredDomain(e.destination.url);
			if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) {
				continue;
			}
			const {cookie} = associatedCookie;
			const cookiesForSourceDomain = mapGetOrCreate(this._cookiesBySourceAndDestinationRegisteredDomain, sourceRegisteredDomain, () => observable.map<string, Map<CookieName, Cookie>>());
			const cookiesForDestinationDomain = mapGetOrCreate(cookiesForSourceDomain, destinationRegisteredDomain, () => observable.map<CookieName, Cookie>());
			cookiesForDestinationDomain.set(cookie.name, cookie);
		}
	});

	processWebRequestSendHeadersEvent = action ( (e: DbRecordedEvent<NetworkEventOnSendHeaders>) => {
		const sourceRegisteredDomain = getRegisteredDomain(e.tab.url);
		const destinationRegisteredDomain = getRegisteredDomain(e.destination.url);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) {
			return;
		}
		const cookieHeader = e.raw.requestHeaders?.find((header) => header.name.toLowerCase() === "cookie");
		if (cookieHeader == null || cookieHeader.value == null) {
			return;
		}
		const cookies = cookieHeader.value.split(";")
			.map( cookieString => {
				const [cookieName, cookieValue] = cookieString.split("=", 2);
				return {name: cookieName?.trim() ?? "", value: cookieValue?.trim() ?? ""};
			})
		const cookiesForSourceDomain = mapGetOrCreate(this._cookiesBySourceAndDestinationRegisteredDomain, sourceRegisteredDomain, () => observable.map<string, Map<CookieName, Cookie>>());
		const cookiesForDestinationDomain = mapGetOrCreate(cookiesForSourceDomain, destinationRegisteredDomain, () => observable.map<CookieName, Cookie>());
		for (const cookie of cookies) {
			cookiesForDestinationDomain.set(cookie.name, cookie);
		}
	});

	processEvent = (e: DbRecordedEvent) => {
		this.highestEventIndexProcessed = Math.max(this.highestEventIndexProcessed, e.eventIndex);
		if (e.category === "Debugger.Network" && e.eventName === "Network.requestWillBeSentExtraInfo") {
			this.processNetworkRequestWillBeSentExtraInfoEvent(e as DbRecordedEvent<NetworkEventDebuggerNetworkRequestWillBeSentExtraInfo>);
		} else if (e.category === "WebRequest" && e.eventName === "WebRequest.sendHeaders") {
			this.processWebRequestSendHeadersEvent(e as DbRecordedEvent<NetworkEventOnSendHeaders>);
		}
		return undefined;
	}

	processEvents = (events: DbRecordedEvent[]) => {
		for (const e of events) {
			this.processEvent(e);
		}
	}

	constructor(public readonly recordingUuid: UUID) {}
}

export class RecordingDatabaseCookiesSent extends RecordingCookiesSent {

	@computed get eventsTableReader() { return DbEventsTableReader.forRecordingUuid(this.recordingUuid); }

	update = executeEventually( async () => {
		const nextHighestEvent = this.highestEventIndexProcessed + 1;
		await Promise.all([
			this.eventsTableReader.forEventsWithCategoryAndName("WebRequest", "WebRequest.sendHeaders", nextHighestEvent)(this.processEvent),
			this.eventsTableReader.forEventsWithCategoryAndName("Debugger.Network", "Network.requestWillBeSentExtraInfo", nextHighestEvent)(this.processEvent)
		])
	});

	constructor(recordingUuid: UUID) {
		super(recordingUuid);
		this.update();
		NewEventAddedToDatabase.addListener(event => {
			if (event.recordingUuid === this.recordingUuid && event.eventIndex > this.highestEventIndexProcessed) {
				this.update();
			}
		});
	}
}